import { useEffect, useState } from 'react';
import {
  ChipBadge,
  deserify,
  getMediaBasePath,
  IStyles,
  NewSectionList,
  NoContentCard,
  pxToRem,
  pxTovW,
  theme,
} from '@geneo2-web/shared-ui';
import {
  Box,
  Button,
  Skeleton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import React from 'react';
import type { IPersonalisedJourneyItem } from '../dummyData/home';
import { PersonalisedJourneyCard } from '../PersonalisedJourneyCard';
import { ShimmerPersonalisedJourneyCard } from '../PersonalisedJourneyCard/ShimmerPersonalisedJourneyCard';
import { getOngoingJourneys } from '../../api/module';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../reduxStore/reduxHooks';
import { PAL_KNOWLEDGE_CONCEPTS } from '../../../../routeHandling/RoutesNomenclature';
import { useLocation, useNavigate } from 'react-router-dom';
import { onChapterSelect } from '../../utils';
import { formatUpiScore } from '../PerformancePopup';

interface IHomePersonalisedJourney {
  onStartJourneyClick?: () => void;
}

const styles: IStyles = {
  sectionListWrapper: {
    paddingTop: pxToRem(10),
    paddingBottom: pxToRem(10),
    display: 'flex',
    flexDirection: 'column',
  },
  sectionTitleWrapper: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    flexDirection: 'row',
    paddingLeft: { xs: pxToRem(22), md: 0 },
    paddingRight: { xs: pxToRem(22), md: 0 },
  },
  capsuleTitleWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  capsule: {
    backgroundColor: 'primary.main',
    borderRadius: pxToRem(19),
    padding: '5px 22px',
    color: 'common.white',
    marginLeft: pxToRem(10),
  },
  headerRight: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(10), md: pxTovW(12) },
  },
  startNewButton: {
    borderColor: '#4153B9',
    color: '#4153B9',
    textTransform: 'none',
    borderRadius: pxToRem(8),
    py: { xs: pxToRem(6), md: pxTovW(8) },
    px: { xs: pxToRem(12), md: pxTovW(14) },
    '&:hover': {
      borderColor: 'primary.dark',
      backgroundColor: 'action.hover',
    },
  },
  seeAll: {
    textDecoration: 'underline',
    color: 'primary.main',
    cursor: 'pointer',
    fontWeight: 500,
  },
  shimmerContent: {
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(10), md: pxTovW(35), lg: pxTovW(18) },
    paddingY: { xs: pxToRem(20), md: pxTovW(40) },
    p: {
      xs: `${pxToRem(20)} ${pxToRem(0)} ${pxToRem(10)}`,
      md: `${pxToRem(20)} ${pxToRem(10)} ${pxToRem(10)}`,
      lg: `${pxTovW(10)} ${pxTovW(10)} ${pxTovW(20)}`,
    },
    width: '100%',
    background: {
      xs: 'linear-gradient(to bottom, white 33%, #E8E4F8 33% 66%, white 66% 100%)',
      md: 'linear-gradient(to bottom, white 20%, #E8E4F8 20% 80%)',
    },
  },
  carouselButtonShimmer: {
    display: { xs: 'none', md: 'block' },
    flexShrink: 0,
    width: { md: pxTovW(49) },
    height: { md: pxTovW(73) },
    borderRadius: { md: pxTovW(8) },
  },
  shimmerCardsWrapper: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    gap: { xs: pxToRem(10), md: pxTovW(35), lg: pxTovW(18) },
  },
};

const PERSONALISED_JOURNEY_GRADIENT =
  'linear-gradient(to bottom, white 33%, #E8E4F8 33% 66%, white 66% 100%)';

const HomePersonalisedJourney = (props: IHomePersonalisedJourney) => {
  const { onStartJourneyClick } = props;
  const [loading, setLoading] = useState<
    'loading' | 'error' | 'completed' | 'mapping-error'
  >('loading');
  const [list, setList] = useState<IPersonalisedJourneyItem[] | null>(null);
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const itemsPerPage = mobile ? 1 : 2;
  const { userInfo } = useAppSelector((state) => deserify(state.auth));
  const { externalStudentId } = useAppSelector((state) => deserify(state.pal));
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const selectedSubjectId = deserify(
    useAppSelector((state) => state.home.selected_subject_id)
  );
  useEffect(() => {
    // const timer = setTimeout(() => {
    getPersonalisedJourneyData();
    // }, 5000);
    // return () => clearTimeout(timer);
  }, [externalStudentId, selectedSubjectId]);

  const getPersonalisedJourneyData = async () => {
    try {
      setLoading('loading');
      if (!externalStudentId) {
        setLoading('mapping-error');
        return;
      }
      const book_id = selectedSubjectId
        ? (userInfo?.learnSubjects || []).find(
            (val) => val.subjectId == selectedSubjectId
          )?.bookId
        : undefined;
      const res = await getOngoingJourneys({
        page: 1,
        page_size: 20,
        book_id,
      });
      const data =
        res.modules?.map((val) => {
          const bookId = val.geneoModule?.book_id;
          const subjectName =
            (userInfo?.learnSubjects || []).find((val) => val.bookId == bookId)
              ?.subjectName || '';
          return {
            moduleId: val.module_id,
            type: 'adaptive',
            posterImageUrl: val.geneoModule?.poster_image_url
              ? getMediaBasePath(
                  val.geneoModule?.poster_image_url,
                  'processedMediaBucket'
                )
              : '',
            class: userInfo?.classSectionDetails?.className || '',
            subject: subjectName,
            mode: 'Adaptive',
            chapterName: val.module_name,
            startDate: val.progress.module_started_at,
            timeSpentInSeconds: val.progress.total_practice_time_seconds,
            masteryPercent: formatUpiScore(
              val.progress.upi_score_aggregate || 0
            ),
          };
        }) || [];
      setList(data);
      setLoading('completed');
    } catch (err) {
      setLoading('error');
    }
  };

  const cardItems =
    list?.map((item, index) => (
      <PersonalisedJourneyCard
        key={`journey_${index}`}
        item={item}
        onClick={() => {
          onChapterSelect(navigate, dispatch, item.moduleId, location.pathname);
        }}
      />
    )) ?? [];

  if (loading == 'loading') {
    const gapMultiplier = itemsPerPage - 1;
    const shimmerSlotFlex = {
      xs: `0 0 ${
        gapMultiplier === 0
          ? '100%'
          : `calc((100% - ${pxToRem(10)} * ${gapMultiplier}) / ${itemsPerPage})`
      }`,
      md: `0 0 ${
        gapMultiplier === 0
          ? '100%'
          : `calc((100% - ${pxTovW(35)} * ${gapMultiplier}) / ${itemsPerPage})`
      }`,
      lg: `0 0 ${
        gapMultiplier === 0
          ? '100%'
          : `calc((100% - ${pxTovW(18)} * ${gapMultiplier}) / ${itemsPerPage})`
      }`,
    };
    return (
      <Box sx={styles.sectionListWrapper}>
        <Box
          sx={{
            ...styles.sectionTitleWrapper,
            marginBottom: { xs: pxToRem(10), md: pxTovW(20) },
          }}
        >
          <Box sx={styles.capsuleTitleWrapper}>
            <Skeleton
              variant="rounded"
              sx={{
                width: { xs: pxToRem(180), md: pxTovW(220) },
                height: { xs: pxToRem(28), md: pxTovW(32) },
                borderRadius: 1,
              }}
            />
          </Box>
        </Box>
        <Box sx={styles.shimmerContent}>
          <Skeleton variant="rounded" sx={styles.carouselButtonShimmer} />
          <Box sx={styles.shimmerCardsWrapper}>
            {Array.from({ length: itemsPerPage }, (_, i) => (
              <Box key={i} sx={{ flex: shimmerSlotFlex, minWidth: 0 }}>
                <ShimmerPersonalisedJourneyCard />
              </Box>
            ))}
          </Box>
          <Skeleton variant="rounded" sx={styles.carouselButtonShimmer} />
        </Box>
      </Box>
    );
  }

  if (loading == 'error' || loading == 'mapping-error') {
    return (
      <Box sx={styles.sectionListWrapper}>
        <Box sx={styles.sectionTitleWrapper}>
          <Box sx={styles.capsuleTitleWrapper}>
            <Typography variant={mobile ? 'h3' : 'h2'} sx={{ mb: '10px' }}>
              Personalised Journey
            </Typography>
          </Box>
        </Box>
        <Box sx={{ paddingTop: { xs: pxToRem(20), md: pxTovW(40) } }}>
          {loading == 'mapping-error' ? (
            <NoContentCard
              variant="info"
              icon="cards"
              text="Student not onboarded to PAL yet"
            />
          ) : (
            <NoContentCard variant="error" icon="cards" text="Error Occurred" />
          )}
        </Box>
      </Box>
    );
  }

  const renderTitleHeader = () => {
    return (
      <Box sx={styles.sectionTitleWrapper}>
        <Box sx={styles.capsuleTitleWrapper}>
          <Typography variant={mobile ? 'h3' : 'h2'} sx={{ mr: '10px' }}>
            Personalised Journey
          </Typography>
        </Box>
        <Box sx={styles.headerRight}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            sx={styles.startNewButton}
            onClick={() => {
              if (onStartJourneyClick) {
                onStartJourneyClick();
              }
            }}
          >
            <Typography variant="body2" color="#4153B9">
              Start New Journey
            </Typography>
          </Button>
          {/* <Typography
            variant="h5"
            sx={{
              color: '#007CDC',
              textDecorationColor: '#007CDC',
              textDecorationLine: 'underline',
              cursor: 'pointer',
            }}
          >
            See All
          </Typography> */}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={styles.sectionListWrapper}>
      {renderTitleHeader()}
      <Box sx={{ p: { lg: `${pxToRem(20)} ${pxToRem(0)}` } }}>
        <NewSectionList
          hideSectionHeader
          sectionTitle=""
          items={cardItems}
          itemsPerPage={itemsPerPage}
          background="#E8E4F8"
          noContentMessage="No personalised journey started"
        />
      </Box>
    </Box>
  );
};

export default HomePersonalisedJourney;
